/** Characters per line at the printer's default font. */
export const RECEIPT_COLUMNS = { 58: 32, 80: 48 };
export const paperWidths = () => [58, 80];
// ---------------------------------------------------------------- layout ----
export function rule(columns, character = '-') {
    return character.repeat(Math.max(0, columns));
}
export function centre(text, columns) {
    if (text.length >= columns)
        return text;
    return ' '.repeat(Math.floor((columns - text.length) / 2)) + text;
}
/** Left text, right text, right edge flush. The left is cut if it must be. */
export function spread(left, right, columns) {
    const room = columns - right.length;
    if (room < 2)
        return right.slice(-columns);
    const trimmed = left.length > room - 1 ? left.slice(0, room - 1) : left;
    return trimmed.padEnd(room) + right;
}
/** Word wrap, falling back to hard breaks for a word longer than the roll. */
export function wrapText(text, columns) {
    const lines = [];
    let line = '';
    const flush = () => {
        if (line !== '')
            lines.push(line);
        line = '';
    };
    for (const word of text.split(/\s+/).filter(Boolean)) {
        let rest = word;
        while (rest.length > columns) {
            flush();
            lines.push(rest.slice(0, columns));
            rest = rest.slice(columns);
        }
        if (rest === '')
            continue;
        if (line === '')
            line = rest;
        else if (line.length + 1 + rest.length <= columns)
            line += ` ${rest}`;
        else {
            flush();
            line = rest;
        }
    }
    flush();
    return lines.length > 0 ? lines : [''];
}
const amountFormatter = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** 50000 -> "500.00". No rupee sign: see the note at the top of this file. */
const amount = (paise) => amountFormatter.format(paise / 100);
/** 2 -> "2", 0.5 -> "0.5" - a kitchen should not read "0.500". */
const count = (quantity) => Number.isInteger(quantity) ? String(quantity) : String(Number(quantity.toFixed(3)));
// --------------------------------------------------------------- receipt ----
/** The bill a guest is handed at the counter. */
export function renderReceipt(data, width) {
    const cols = RECEIPT_COLUMNS[width];
    const out = [];
    const add = (line = '') => out.push(line);
    const middle = (text) => wrapText(text, cols).forEach((l) => add(centre(l, cols)));
    middle(data.property.name);
    const where = [data.property.address, data.property.city].filter(Boolean).join(', ');
    middle([where, data.property.pincode].filter(Boolean).join(' '));
    if (data.property.phone)
        middle(`Ph ${data.property.phone}`);
    if (data.property.gstin)
        middle(`GSTIN ${data.property.gstin}`);
    if (data.property.fssai)
        middle(`FSSAI ${data.property.fssai}`);
    add();
    middle(data.title);
    add(rule(cols));
    add(`Bill : ${data.number}`);
    add(`Date : ${data.issuedAt}`);
    if (data.orderType)
        add(`Order: ${data.orderType}`);
    if (data.guestName)
        add(`Guest: ${data.guestName}`);
    if (data.paymentMode)
        add(`Paid : ${data.paymentMode}`);
    add(rule(cols));
    add(spread('Item', 'Amount', cols));
    add(rule(cols));
    for (const item of data.items) {
        wrapText(item.name, cols).forEach((line) => add(line));
        add(spread(`  ${count(item.quantity)} x ${amount(item.unitPrice)}`, amount(item.amount), cols));
    }
    add(rule(cols));
    const total = (label, value, sign = '') => add(spread(label, `${sign}${amount(value)}`, cols));
    total('Subtotal', data.totals.subtotal);
    // Named first, so a guest reading the roll can see which offer took what
    // rather than a single unexplained number.
    for (const offer of data.offers ?? []) {
        wrapText(offer.name, cols - 10).forEach((part, index) => {
            if (index === 0)
                add(spread(part, `-${amount(offer.discount)}`, cols));
            else
                add(part);
        });
    }
    const counterDiscount = data.totals.discount - (data.offers ?? []).reduce((sum, offer) => sum + offer.discount, 0);
    if (counterDiscount > 0)
        add(spread('Discount', `-${amount(counterDiscount)}`, cols));
    if (data.totals.cgst > 0)
        total('CGST', data.totals.cgst);
    if (data.totals.sgst > 0)
        total('SGST', data.totals.sgst);
    if (data.totals.igst > 0)
        total('IGST', data.totals.igst);
    if (data.totals.roundOff !== 0)
        total('Round off', data.totals.roundOff);
    add(rule(cols));
    total('TOTAL', data.totals.total);
    add(rule(cols, '='));
    add();
    middle(data.footer ?? 'Thank you, visit again');
    add();
    add();
    return `${out.join('\n')}\n`;
}
/** The slip the kitchen cooks from. Dishes and counts, never money. */
export function renderKot(data, width) {
    const cols = RECEIPT_COLUMNS[width];
    const out = [];
    const add = (line = '') => out.push(line);
    const middle = (text) => wrapText(text, cols).forEach((l) => add(centre(l, cols)));
    middle(data.property.name);
    middle('KOT');
    add(rule(cols));
    add(`Order: ${data.number}`);
    add(`Time : ${data.issuedAt}`);
    if (data.orderType)
        add(`For  : ${data.orderType}`);
    if (data.guestName)
        add(`Guest: ${data.guestName}`);
    add(rule(cols));
    for (const item of data.items) {
        const [first, ...rest] = wrapText(item.name, cols - 4);
        add(`${count(item.quantity).padStart(2)}  ${first ?? ''}`);
        rest.forEach((line) => add(`    ${line}`));
        if (item.note)
            wrapText(`* ${item.note}`, cols - 4).forEach((line) => add(`    ${line}`));
    }
    add(rule(cols));
    add();
    add();
    return `${out.join('\n')}\n`;
}
//# sourceMappingURL=receipt.js.map